import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

import { Form, FormField, FormItem, FormLabel, FormMessage, FormControl } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

// Form validation schema
const formSchema = z.object({
  carbon_credits_exchange_rate: z.number().min(0, "Exchange rate must be 0 or greater"),
  carbon_credits_exchange_threshold: z.number().min(0, "Exchange threshold must be 0 or greater"),
  carbon_offset_to_credit_factor: z.number().min(0, "Credit factor must be 0 or greater"),
  
  // Car emissions per km
  car_emissions_co2_per_km: z.number().min(0, "Car CO2 emissions must be 0 or greater"),
  car_emissions_ch4_per_km: z.number().min(0, "Car CH4 emissions must be 0 or greater"),
  car_emissions_n2o_per_km: z.number().min(0, "Car N2O emissions must be 0 or greater"),
  
  // GWP (Global Warming Potential)
  gwp_co2: z.number().min(0, "GWP CO2 must be 0 or greater"),
  gwp_ch4: z.number().min(0, "GWP CH4 must be 0 or greater"),
  gwp_n2o: z.number().min(0, "GWP N2O must be 0 or greater"),
  
  // Grid emissions per kWh
  grid_emissions_co2_per_kwh: z.number().min(0, "Grid CO2 emissions must be 0 or greater"),
  grid_emissions_ch4_per_kwh: z.number().min(0, "Grid CH4 emissions must be 0 or greater"),
  grid_emissions_n2o_per_kwh: z.number().min(0, "Grid N2O emissions must be 0 or greater"),
});

type FormValues = z.infer<typeof formSchema>;

const CarbonCreditSettings: React.FC = () => {
  const queryClient = useQueryClient();

  // Fetch current settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ["carbon-credit-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_settings")
        .select("*")
        .in("name", [
          "carbon_credits_exchange_rate", 
          "carbon_credits_exchange_threshold", 
          "carbon_offset_to_credit_factor",
          "car_emissions_co2_per_km",
          "car_emissions_ch4_per_km",
          "car_emissions_n2o_per_km",
          "gwp_co2",
          "gwp_ch4",
          "gwp_n2o",
          "grid_emissions_co2_per_kwh",
          "grid_emissions_ch4_per_kwh",
          "grid_emissions_n2o_per_kwh"
        ]);
      if (error) throw error;
      return data;
    },
  });

  // Update settings mutation
  const { mutate: updateSettings, isPending } = useMutation({
    mutationFn: async (values: FormValues) => {
      // Update each setting individually
      for (const [name, value] of Object.entries(values)) {
        console.log("Updating", name, "to", value);
        const { error } = await supabase
          .from("admin_settings")
          .update({ value: value.toString() })
          .eq("name", name);
        if (error) {
          console.error("Supabase update error:", error);
          throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["carbon-credit-settings"] });
      toast.success("Carbon credit settings updated successfully");
    },
    onError: (error) => {
      console.error("Error updating carbon credit settings:", error);
      toast.error("Failed to update carbon credit settings");
    },
  });

  // Initialize form with current values (convert to number)
  const getSettingValue = (name: string, defaultValue: number) => {
    const setting = settings?.find(s => s.name === name)?.value;
    return setting ? Number(setting) : defaultValue;
  };
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      carbon_credits_exchange_rate: 12,
      carbon_credits_exchange_threshold: 10,
      carbon_offset_to_credit_factor: 0.001,
      car_emissions_co2_per_km: 0.1620400,
      car_emissions_ch4_per_km: 0.0000129,
      car_emissions_n2o_per_km: 0.0000012,
      gwp_co2: 1,
      gwp_ch4: 28,
      gwp_n2o: 265,
      grid_emissions_co2_per_kwh: 0.17489,
      grid_emissions_ch4_per_kwh: 0.0000321,
      grid_emissions_n2o_per_kwh: 0.0000046,
    },
  });

  // Update form values when settings are loaded
  React.useEffect(() => {
    if (settings) {
      form.reset({
        carbon_credits_exchange_rate: getSettingValue("carbon_credits_exchange_rate", 12),
        carbon_credits_exchange_threshold: getSettingValue("carbon_credits_exchange_threshold", 10),
        carbon_offset_to_credit_factor: getSettingValue("carbon_offset_to_credit_factor", 0.001),
        car_emissions_co2_per_km: getSettingValue("car_emissions_co2_per_km", 0.1620400),
        car_emissions_ch4_per_km: getSettingValue("car_emissions_ch4_per_km", 0.0000129),
        car_emissions_n2o_per_km: getSettingValue("car_emissions_n2o_per_km", 0.0000012),
        gwp_co2: getSettingValue("gwp_co2", 1),
        gwp_ch4: getSettingValue("gwp_ch4", 28),
        gwp_n2o: getSettingValue("gwp_n2o", 265),
        grid_emissions_co2_per_kwh: getSettingValue("grid_emissions_co2_per_kwh", 0.17489),
        grid_emissions_ch4_per_kwh: getSettingValue("grid_emissions_ch4_per_kwh", 0.0000321),
        grid_emissions_n2o_per_kwh: getSettingValue("grid_emissions_n2o_per_kwh", 0.0000046),
      });
    }
  }, [settings, form]);

  const onSubmit = (values: FormValues) => {
    updateSettings(values);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center my-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Carbon Credit Settings</CardTitle>
        <CardDescription>
          Configure carbon credit exchange rates, emission factors, and environmental coefficients for accurate carbon footprint calculations
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Carbon Credits Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Carbon Credits</h3>
              <div className="grid gap-6">
                <FormField
                  control={form.control}
                  name="carbon_credits_exchange_rate"
                  render={({ field }) => {
                    const [inputValue, setInputValue] = React.useState(
                      field.value ? field.value.toString() : ""
                    );

                    React.useEffect(() => {
                      setInputValue(field.value ? field.value.toString() : "");
                    }, [field.value]);

                    return (
                      <FormItem>
                        <FormLabel>Exchange Rate (£ per credit)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="12.00"
                            disabled={isPending}
                            value={inputValue}
                            onChange={(e) => {
                              const value = e.target.value;
                              setInputValue(value);
                              field.onChange(value === "" ? "" : Number(value));
                            }}
                            onBlur={() => {
                              // Format the display value when user finishes typing
                              if (inputValue && !isNaN(Number(inputValue))) {
                                setInputValue(Number(inputValue).toString());
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                
                <FormField
                  control={form.control}
                  name="carbon_credits_exchange_threshold"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Exchange Threshold (credits)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.00001"
                          min="0"
                          placeholder="10"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="carbon_offset_to_credit_factor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Carbon Offset to Credit Factor</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.00001"
                          min="0"
                          placeholder="0.001"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Car Emissions Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Car Emissions per km</h3>
              <div className="grid gap-6">
                <FormField
                  control={form.control}
                  name="car_emissions_co2_per_km"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CO2 (kg/km)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.0000001"
                          min="0"
                          placeholder="0.1620400"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="car_emissions_ch4_per_km"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CH4 (kg/km)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.0000001"
                          min="0"
                          placeholder="0.0000129"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="car_emissions_n2o_per_km"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>N2O (kg/km)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.0000001"
                          min="0"
                          placeholder="0.0000012"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* GWP Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Global Warming Potential (GWP)</h3>
              <div className="grid gap-6">
                <FormField
                  control={form.control}
                  name="gwp_co2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CO2 GWP</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          placeholder="1"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gwp_ch4"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CH4 GWP</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          placeholder="28"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gwp_n2o"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>N2O GWP</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          placeholder="265"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Grid Emissions Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Grid Emissions per kWh</h3>
              <div className="grid gap-6">
                <FormField
                  control={form.control}
                  name="grid_emissions_co2_per_kwh"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CO2 (kg/kWh)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.0000001"
                          min="0"
                          placeholder="0.17489"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="grid_emissions_ch4_per_kwh"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CH4 (kg/kWh)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.0000001"
                          min="0"
                          placeholder="0.0000321"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="grid_emissions_n2o_per_kwh"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>N2O (kg/kWh)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.0000001"
                          min="0"
                          placeholder="0.0000046"
                          disabled={isPending}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? "" : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default CarbonCreditSettings; 